import { z } from 'zod';
import { HOURS_FORMATS, USER_ROLES } from '../enums.js';
import { isoDateSchema, timeZoneSchema, uuidSchema } from './common.js';
/**
 * Small deny-list of extremely common passwords / patterns (NIST 800-63B recommends
 * checking against breached/common lists rather than composition rules).
 */
const COMMON_PASSWORDS = new Set([
    'password',
    'password1',
    'password123',
    'password1234',
    'passwordpassword',
    '123456789012',
    '1234567890123',
    'qwertyuiop12',
    'qwertyuiopasdf',
    'iloveyou1234',
    'letmeinletmein',
    'welcome12345',
    'administrator',
    'changemechangeme',
]);
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const passwordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
    .max(PASSWORD_MAX_LENGTH, `Password must be at most ${PASSWORD_MAX_LENGTH} characters`)
    .refine((p) => !COMMON_PASSWORDS.has(p.toLowerCase()), 'This password is too common')
    .refine((p) => !/^(.)\1+$/.test(p), 'Password cannot be a single repeated character');
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const loginSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const tokenSchema = z.string().min(20).max(200);
export const resetPasswordSchema = z.object({
    token: tokenSchema,
    newPassword: passwordSchema,
});
export const acceptInviteSchema = z.object({
    token: tokenSchema,
    password: passwordSchema,
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
    newPassword: passwordSchema,
});
export const organizationSelfSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    weekStartDay: z.number().int().min(0).max(6),
    hoursFormat: z.enum(HOURS_FORMATS),
    defaultTimeZone: z.string(),
    locale: z.string(),
    /**
     * The closed period, so the timesheet can grey out what it will not be allowed to save
     * (`isTimesheetLocked`). It is organization policy rather than anything private, and the
     * browser needs it to say *why* a cell is read-only instead of just failing on submit.
     */
    timesheetLockDate: isoDateSchema.nullable(),
});
export const userSelfSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.enum(USER_ROLES),
    timeZone: z.string(),
    locale: z.string().nullable(),
    /** Start an empty week with the rows this user used most recently (FR2). */
    showPreviousActivities: z.boolean(),
    /** An administrator reset this password; the user must choose a new one before anything else. */
    mustChangePassword: z.boolean(),
    organization: organizationSelfSchema,
    groups: z.array(z.object({ id: uuidSchema, name: z.string() })),
});
export const updateMeSchema = z
    .object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    timeZone: timeZoneSchema,
    locale: z.string().trim().min(2).max(10).nullable(),
    showPreviousActivities: z.boolean(),
})
    .partial();
export const sessionInfoSchema = z.object({
    id: uuidSchema,
    createdAt: z.string(),
    lastSeenAt: z.string(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    current: z.boolean(),
});
export const invitePreviewSchema = z.object({
    email: z.string(),
    firstName: z.string(),
    organizationName: z.string(),
});
//# sourceMappingURL=auth.js.map